import React, { useRef, useState } from 'react';
import { ArrowUpTrayIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ACCEPTED_DOC_EXT,
  formatFileSize,
  isAcceptedDocFile,
  MAX_DOC_FILE_MB,
} from '../../utils/fleetDocuments.ts';

export type ExistingDocFile = {
  fileUrl: string;
  fileName: string;
  fileSize?: number | null;
  fileMime?: string | null;
  uploadedAt?: string | null;
};

type Props = {
  file: File | null;
  existing?: ExistingDocFile | null;
  onFileChange: (file: File | null) => void;
  onClearExisting?: () => void;
  disabled?: boolean;
};

const DocumentUploadZone: React.FC<Props> = ({
  file,
  existing,
  onFileChange,
  onClearExisting,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');

  const validateAndSet = (next: File | null) => {
    setError('');
    if (!next) {
      onFileChange(null);
      return;
    }
    if (!isAcceptedDocFile(next)) {
      setError('Formato inválido. Use PDF, JPG ou PNG.');
      return;
    }
    if (next.size > MAX_DOC_FILE_MB * 1024 * 1024) {
      setError(`Arquivo muito grande. Máximo ${MAX_DOC_FILE_MB} MB.`);
      return;
    }
    onFileChange(next);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) validateAndSet(dropped);
  };

  const showExisting = !file && existing?.fileUrl;

  return (
    <div className="space-y-2">
      {showExisting ? (
        <div className="rounded-xl border-2 border-dashed border-primary-300 bg-primary-50/40 p-4">
          <p className="text-sm font-medium text-gray-900">Arquivo anexado</p>
          <p className="text-xs text-gray-500 mb-3">Substitua se precisar renovar o documento.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white border border-gray-200 rounded-xl px-3 py-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <DocumentIcon className="h-8 w-8 text-primary-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{existing!.fileName}</p>
                <p className="text-xs text-gray-500">
                  {[
                    formatFileSize(existing!.fileSize),
                    existing!.fileMime?.includes('pdf')
                      ? 'PDF'
                      : existing!.fileMime?.includes('png')
                        ? 'PNG'
                        : existing!.fileMime?.includes('jpeg') || existing!.fileMime?.includes('jpg')
                          ? 'JPG'
                          : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
                className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
              >
                Trocar arquivo
              </button>
              {onClearExisting && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={onClearExisting}
                  className="p-2 text-gray-500 hover:text-red-600"
                  title="Remover arquivo"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">PDF, JPG ou PNG · até {MAX_DOC_FILE_MB} MB</p>
        </div>
      ) : file ? (
        <div className="rounded-xl border-2 border-dashed border-primary-300 bg-primary-50/40 p-4">
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-3 py-3">
            <DocumentIcon className="h-8 w-8 text-primary-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)} · pronto para enviar</p>
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => validateAndSet(null)}
              className="p-2 text-gray-500 hover:text-red-600"
              title="Remover"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="mt-3 text-sm text-primary-700 font-medium"
          >
            Escolher outro arquivo
          </button>
        </div>
      ) : (
        <div
          onDragEnter={e => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragOver={e => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={e => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={onDrop}
          className={`rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-gray-50'
          }`}
        >
          <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-800">Arraste o arquivo ou clique para enviar</p>
          <p className="text-xs text-gray-500 mt-1 mb-4">
            Anexe apólice, CRLV, comprovante de IPVA ou outro documento do veículo.
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Selecionar arquivo
          </button>
          <p className="text-xs text-gray-500 mt-3">PDF, JPG ou PNG · até {MAX_DOC_FILE_MB} MB</p>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={[...ACCEPTED_DOC_EXT, 'application/pdf', 'image/jpeg', 'image/png'].join(',')}
        disabled={disabled}
        onChange={e => {
          const next = e.target.files?.[0] || null;
          validateAndSet(next);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default DocumentUploadZone;
